export type AppView = "tickets" | "today" | "actions" | "memos";
export type TicketStatus = "active" | "waiting" | "closed";
export type ActionLane = "urgent" | "later" | "waiting" | "hold";
export type ReminderMode = "persistent" | "once";

export interface Versioned {
  id: string;
  workspace_id: string;
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface Ticket extends Versioned {
  parent_id: string | null;
  folder_name: string;
  title: string;
  status: TicketStatus;
  area: string;
  project: string;
  type: string;
  purpose: string;
  conclusion: string;
  next_action: string;
  readme_markdown: string;
}

export interface TicketRelation {
  id: string;
  workspace_id: string;
  source_ticket_id: string;
  target_ticket_id: string;
  created_at: string;
}

export interface TicketFile extends Versioned {
  ticket_id: string;
  relative_path: string;
  display_name: string;
  mime_type: string;
  byte_size: number;
  kind: "page" | "media";
  content: string;
  storage_key: string | null;
  storage_mode: "database" | "local_only" | "snapshot" | "synced";
  cloud_status: "database" | "local_only" | "pending" | "uploading" | "available" | "error" | "deleted";
  current_version_id: string | null;
  latest_local_sha256: string | null;
}

export interface Action extends Versioned {
  parent_id: string | null;
  ticket_id: string | null;
  ticket_key: string | null;
  project_name: string;
  context: string;
  reference_url: string;
  text: string;
  lane: ActionLane;
  note: string;
  reminder_on: string | null;
  reminder_mode: ReminderMode;
  today_override_on?: string | null;
  today_override_visible?: boolean | null;
  sort_order: number;
  completed_at: string | null;
}

export interface TicketCatalog {
  ticket_key: string;
  workspace_id: string;
  title: string;
  status: TicketStatus;
  area: string;
  project: string;
  type: string;
  parent_ticket_key: string | null;
  related_ticket_keys: string[];
  reference_url: string;
  readme_markdown: string;
  updated_at_local: string | null;
  synced_at: string;
}

export interface DailyNote extends Versioned {
  note_date: string;
  markdown: string;
}

export interface Memo extends Versioned {
  title: string;
  body: string;
  pinned: boolean;
  sort_order: number;
}

export interface DashboardData {
  tickets: Ticket[];
  ticketCatalog: TicketCatalog[];
  ticketFiles: TicketFile[];
  ticketRelations: TicketRelation[];
  actions: Action[];
  daily: DailyNote | null;
  memos: Memo[];
}

export interface ActionDraft {
  text: string;
  lane: ActionLane;
  note?: string;
  parent_id?: string | null;
  ticket_id?: string | null;
  ticket_key?: string | null;
  project_name?: string;
  context?: string;
  reference_url?: string;
  reminder_on?: string | null;
  reminder_mode?: ReminderMode;
  today_override_on?: string | null;
  today_override_visible?: boolean | null;
}

export type ActionMoveDestination = {
  kind: "before" | "after" | "child";
  target_id: string;
  target_revision: number;
};

export interface TicketDraft {
  parent_id?: string | null;
  title: string;
  folder_name: string;
  status: TicketStatus;
  area: string;
  project: string;
  type: string;
  purpose: string;
  conclusion: string;
  next_action: string;
  readme_markdown: string;
}

export interface TicketFileDraft {
  ticket_id: string;
  relative_path: string;
  title: string;
  content: string;
}

export interface MemoDraft {
  title: string;
  body: string;
  pinned?: boolean;
}

export interface DataRepository {
  getDashboard(date: string): Promise<DashboardData>;
  createAction(input: ActionDraft): Promise<Action>;
  updateAction(id: string, revision: number, input: Partial<ActionDraft> & { completed?: boolean }): Promise<Action>;
  moveAction(action: Pick<Action, "id" | "revision">, destination: ActionMoveDestination): Promise<void>;
  changeActionTicketContext(action: Pick<Action, "id" | "revision">, children: Pick<Action, "id" | "revision">[], next: { ticket_key: string | null; project_name: string; context: string }): Promise<number>;
  changeActionLane(action: Pick<Action, "id" | "revision">, nextLane: ActionLane, destination?: { id: string; revision: number; placement: "before" | "after" }): Promise<number>;
  scheduleActionsForDate(actions: Pick<Action, "id" | "revision">[], date: string): Promise<void>;
  reorderActions(actionIds: string[]): Promise<void>;
  createTicket(input: TicketDraft): Promise<Ticket>;
  updateTicket(id: string, revision: number, input: Partial<TicketDraft>): Promise<Ticket>;
  createTicketFile(input: TicketFileDraft): Promise<TicketFile>;
  updateTicketFile(id: string, revision: number, input: Partial<TicketFileDraft>): Promise<TicketFile>;
  deleteTicketFile(file: TicketFile): Promise<void>;
  uploadTicketFile(ticketId: string, file: File): Promise<TicketFile>;
  downloadTicketFile(file: TicketFile): Promise<Blob>;
  saveDaily(date: string, markdown: string, revision?: number): Promise<DailyNote>;
  createMemo(input: MemoDraft): Promise<Memo>;
  updateMemo(id: string, revision: number, input: Partial<MemoDraft>): Promise<Memo>;
  reorderMemos(memoIds: string[]): Promise<void>;
  trash(entity: "ticket" | "action" | "memo", record: Ticket | Action | Memo): Promise<void>;
  subscribe(onChange: () => void): () => void;
}

export class ConflictError extends Error {
  constructor() {
    super("別の画面で更新されました。最新の内容を読み込み、もう一度編集してください。");
    this.name = "ConflictError";
  }
}
